import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PageContainer from "../common/PageContainer";
import PageHeader from "../common/PageHeader";
import StatsCard from "../common/StatsCard";
import StatusChip from "../common/StatusChip";
import EmptyState from "../common/EmptyState";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import GroupsIcon from "@mui/icons-material/Groups";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatTime = (date) =>
  new Date(date).toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour12: true,
    hour: "2-digit",
    minute: "2-digit",
  });

const ManagerAttendanceApprovals = () => {
  const [searchParams] = useSearchParams();
  const highlightRequestId = searchParams.get("requestId");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [remarks, setRemarks] = useState({});
  const [actingOnId, setActingOnId] = useState(null);
  const [deepLinkPrepared, setDeepLinkPrepared] = useState(false);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError("");
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/api/attendance/manager-approvals`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.msg || "Failed to fetch attendance approval requests");
      }

      setRequests(data.requests || []);
    } catch (err) {
      setError(err.message || "Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    if (!highlightRequestId || deepLinkPrepared) return;
    setFilterStatus("all");
    setFilterType("all");
    setSearchTerm("");
    setDeepLinkPrepared(true);
  }, [highlightRequestId, deepLinkPrepared]);

  useEffect(() => {
    if (!highlightRequestId || loading || requests.length === 0) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(
        `attendance-approval-row-${highlightRequestId}`
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.style.transition = "background-color 0.4s ease";
        el.style.backgroundColor = "#fffbeb";
        setTimeout(() => {
          el.style.backgroundColor = "";
        }, 2800);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightRequestId, loading, requests]);

  const filteredRequests = useMemo(
    () =>
      requests.filter((request) => {
        const matchesSearch =
          request.employee?.name
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          request.employee?.employeeid
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase());
        const matchesStatus =
          filterStatus === "all" || request.status === filterStatus;
        const matchesType =
          filterType === "all" || request.type === filterType;
        return matchesSearch && matchesStatus && matchesType;
      }),
    [requests, searchTerm, filterStatus, filterType]
  );

  const handleApprove = async (id) => {
    try {
      setError("");
      setActingOnId(id);
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/attendance/manager-approvals/${id}/approve`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.msg || "Failed to approve");
      }

      setRequests((prev) =>
        prev.map((r) => (r._id === id ? { ...r, status: "approved" } : r))
      );
    } catch (err) {
      setError(err.message || "Failed to approve");
    } finally {
      setActingOnId(null);
    }
  };

  const handleReject = async (id) => {
    try {
      setError("");
      setActingOnId(id);
      const token = sessionStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}/api/attendance/manager-approvals/${id}/reject`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ managerRemark: remarks[id] || "" }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.msg || "Failed to reject");
      }

      setRequests((prev) =>
        prev.map((r) =>
          r._id === id
            ? { ...r, status: "rejected", managerRemark: remarks[id] || "" }
            : r
        )
      );
    } catch (err) {
      setError(err.message || "Failed to reject");
    } finally {
      setActingOnId(null);
    }
  };

  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return { total, pending, approved, rejected };
  }, [requests]);

  return (
    <PageContainer>
      <PageHeader
        gradient
        title="Attendance Approvals"
        subtitle="Approve or reject outside-office check-in / check-out requests"
        icon={<AssignmentIcon />}
        actions={
          <IconButton
            onClick={fetchRequests}
            disabled={loading}
            size="small"
            sx={{
              border: "1px solid #eef0f6",
              borderRadius: "8px",
              p: 1,
              "&:hover": { bgcolor: "#f5f7fb" },
            }}
            aria-label="Refresh"
          >
            <RefreshIcon fontSize="small" sx={{ color: "#8b8fa3" }} />
          </IconButton>
        }
      />

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Total Requests"
            value={stats.total}
            icon={<GroupsIcon />}
            color="#667eea"
            bgColor="#EBF0FF"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Pending"
            value={stats.pending}
            icon={<PendingActionsIcon />}
            color="#92400E"
            bgColor="#FEF3C7"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Approved"
            value={stats.approved}
            icon={<CheckCircleIcon />}
            color="#059669"
            bgColor="#D1FAE5"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatsCard
            label="Rejected"
            value={stats.rejected}
            icon={<CancelIcon />}
            color="#DC2626"
            bgColor="#FEE2E2"
          />
        </Grid>
      </Grid>

      {/* Search / Filter Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #eef0f6",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="stretch">
          <TextField
            placeholder="Search by name or employee ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.875rem" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#8b8fa3" }} />
                </InputAdornment>
              ),
            }}
          />
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 160 }, borderRadius: "8px", fontSize: "0.875rem" }}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="checkin">Check-in</MenuItem>
            <MenuItem value="checkout">Check-out</MenuItem>
          </Select>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 160 }, borderRadius: "8px", fontSize: "0.875rem" }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #eef0f6",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={44} />
              <Skeleton variant="rounded" height={44} />
              <Skeleton variant="rounded" height={44} />
            </Stack>
          </Box>
        ) : filteredRequests.length === 0 ? (
          <EmptyState
            title="No approval requests"
            subtitle="There are no attendance approval requests matching your filters."
          />
        ) : (
          <>
            {/* Desktop Table */}
            <TableContainer sx={{ display: { xs: "none", lg: "block" }, overflowX: "auto", maxWidth: "100%" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Employee", "Type", "Date", "Time", "Reason", "Location", "Status", "Remark", "Action"].map(
                      (header) => (
                        <TableCell
                          key={header}
                          sx={{
                            bgcolor: "#f5f7fb",
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            color: "#5a5f7a",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            py: 1.5,
                            borderBottom: "2px solid #f0f0f5",
                          }}
                        >
                          {header}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.map((request) => (
                    <TableRow
                      key={request._id}
                      id={`attendance-approval-row-${request._id}`}
                      sx={{ "&:hover": { bgcolor: "#f5f7fb" } }}
                    >
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            sx={{
                              bgcolor: "#eef0ff",
                              color: "#667eea",
                              width: 34,
                              height: 34,
                              fontSize: "0.875rem",
                              fontWeight: 600,
                            }}
                          >
                            {request.employee?.name?.charAt(0)?.toUpperCase() || "E"}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                              {request.employee?.name}
                            </Typography>
                            <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                              {request.employee?.employeeid}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "6px",
                            bgcolor: request.type === "checkin" ? "#EBF0FF" : "#F5F3FF",
                            color: request.type === "checkin" ? "#667eea" : "#6D28D9",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                          }}
                        >
                          {request.type === "checkin" ? (
                            <LoginIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <LogoutIcon sx={{ fontSize: 14 }} />
                          )}
                          {request.type === "checkin" ? "Check-in" : "Check-out"}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        {formatDate(request.requestedAt)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2 }}>
                        {formatTime(request.requestedAt)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2, maxWidth: 160 }}>
                        {request.reason}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.875rem", color: "#1a1a2e", py: 2, maxWidth: 180 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <LocationOnIcon sx={{ fontSize: 14, color: "#8b8fa3" }} />
                          <Typography variant="body2" noWrap sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                            {request.location}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <StatusChip status={request.status} />
                      </TableCell>
                      <TableCell sx={{ py: 2, minWidth: 170 }}>
                        {request.status === "pending" ? (
                          <TextField
                            size="small"
                            fullWidth
                            placeholder="Remark (optional)"
                            value={remarks[request._id] ?? ""}
                            onChange={(e) =>
                              setRemarks((prev) => ({
                                ...prev,
                                [request._id]: e.target.value,
                              }))
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "8px",
                                fontSize: "0.8125rem",
                              },
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>
                            {request.managerRemark || "\u2014"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        {request.status === "pending" && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              disabled={actingOnId === request._id}
                              onClick={() => handleApprove(request._id)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                                fontSize: "0.8125rem",
                                px: 2,
                                py: 0.5,
                                bgcolor: "#059669",
                                color: "#fff",
                                "&:hover": { bgcolor: "#047857" },
                                "&:disabled": { bgcolor: "#D1FAE5", color: "#065F46" },
                              }}
                            >
                              {actingOnId === request._id ? "..." : "Approve"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={actingOnId === request._id}
                              onClick={() => handleReject(request._id)}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: "8px",
                                fontSize: "0.8125rem",
                                px: 2,
                                py: 0.5,
                                borderColor: "#DC2626",
                                color: "#DC2626",
                                "&:hover": {
                                  borderColor: "#B91C1C",
                                  bgcolor: "#FEF2F2",
                                },
                              }}
                            >
                              {actingOnId === request._id ? "..." : "Reject"}
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Mobile Cards */}
            <Box sx={{ display: { xs: "block", lg: "none" }, p: 2 }}>
              <Grid container spacing={2}>
                {filteredRequests.map((request) => (
                  <Grid item xs={12} key={request._id}>
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "16px",
                        border: "1px solid #eef0f6",
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ px: 2.5, py: 2, bgcolor: "#f5f7fb", borderBottom: "2px solid #f0f0f5" }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <Avatar
                              sx={{
                                bgcolor: "#eef0ff",
                                color: "#667eea",
                                width: 34,
                                height: 34,
                                fontSize: "0.875rem",
                                fontWeight: 600,
                              }}
                            >
                              {request.employee?.name?.charAt(0)?.toUpperCase() || "E"}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {request.employee?.name}
                              </Typography>
                              <Typography sx={{ fontSize: "0.75rem", color: "#8b8fa3" }}>
                                {request.employee?.employeeid}
                              </Typography>
                            </Box>
                          </Box>
                          <StatusChip status={request.status} />
                        </Stack>
                      </Box>

                      <Box sx={{ p: 2.5 }}>
                        <Stack spacing={1.5}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Type</Typography>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5,
                                px: 1.5,
                                py: 0.25,
                                borderRadius: "6px",
                                bgcolor: request.type === "checkin" ? "#EBF0FF" : "#F5F3FF",
                                color: request.type === "checkin" ? "#667eea" : "#6D28D9",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                              }}
                            >
                              {request.type === "checkin" ? "Check-in" : "Check-out"}
                            </Box>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3" }}>Date & Time</Typography>
                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1a1a2e" }}>
                              {formatDate(request.requestedAt)} {formatTime(request.requestedAt)}
                            </Typography>
                          </Box>
                          <Divider />
                          <Box>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>Reason</Typography>
                            <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e", wordBreak: "break-word" }}>
                              {request.reason}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.5 }}>Location</Typography>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <LocationOnIcon sx={{ fontSize: 14, color: "#8b8fa3" }} />
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {request.location}
                              </Typography>
                            </Stack>
                          </Box>

                          {request.status === "pending" ? (
                            <>
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Remark (optional)"
                                value={remarks[request._id] ?? ""}
                                onChange={(e) =>
                                  setRemarks((prev) => ({
                                    ...prev,
                                    [request._id]: e.target.value,
                                  }))
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "0.8125rem" },
                                }}
                              />
                              <Stack direction="row" spacing={1}>
                                <Button
                                  fullWidth
                                  size="small"
                                  disabled={actingOnId === request._id}
                                  onClick={() => handleApprove(request._id)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    py: 1,
                                    bgcolor: "#059669",
                                    color: "#fff",
                                    "&:hover": { bgcolor: "#047857" },
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  disabled={actingOnId === request._id}
                                  onClick={() => handleReject(request._id)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 600,
                                    borderRadius: "8px",
                                    py: 1,
                                    borderColor: "#DC2626",
                                    color: "#DC2626",
                                    "&:hover": { borderColor: "#B91C1C", bgcolor: "#FEF2F2" },
                                  }}
                                >
                                  Reject
                                </Button>
                              </Stack>
                            </>
                          ) : (
                            <Box>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#8b8fa3", mb: 0.25 }}>
                                Manager remark
                              </Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#1a1a2e" }}>
                                {request.managerRemark || "\u2014"}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        )}
      </Paper>
    </PageContainer>
  );
};

export default ManagerAttendanceApprovals;
